import React, { useState, useEffect, useMemo } from 'react';
import { useStore } from 'reactflow';
import { X, Trash2, MessageCircle, Clock, GitBranch, Zap, Square, Image, Upload, Link, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Variable fields available for mapping
const VARIABLE_FIELDS = {
  customer: [
    { value: 'customer.first_name', label: 'First Name' },
    { value: 'customer.last_name', label: 'Last Name' },
    { value: 'customer.full_name', label: 'Full Name' },
    { value: 'customer.email', label: 'Email' },
    { value: 'customer.phone', label: 'Phone' },
  ],
  order: [
    { value: 'order.number', label: 'Order Number' },
    { value: 'order.total', label: 'Order Total' },
    { value: 'order.items_count', label: 'Items Count' },
    { value: 'order.status', label: 'Order Status' },
    { value: 'order.tracking_token', label: 'Tracking Token' },
    { value: 'order.tracking_link', label: 'Opswarex Tracking Link' },
  ],
  production: [
    { value: 'order.production_expected_date', label: 'Expected Ready Date (e.g. 14 Oct 2026)' },
    { value: 'order.production_expected_date_raw', label: 'Expected Ready Date (YYYY-MM-DD)' },
    { value: 'order.production_notes', label: 'Production Notes' },
  ],
  delivery_estimate: [
    { value: 'order.delivery_estimate', label: 'Estimated Delivery Range (e.g. 26 Sep – 27 Sep)' },
    { value: 'order.delivery_estimate_min', label: 'Estimated Delivery (earliest)' },
    { value: 'order.delivery_estimate_max', label: 'Estimated Delivery (latest)' },
  ],
  // Dedicated AWB group — short, literal names so WhatsApp/Email templates
  // read cleanly ("Your AWB {{awb.number}} is ready" instead of the longer
  // fulfillment namespace). Populated by the backend on every courier-
  // related journey trigger (awb_generated, courier_assigned,
  // courier_picked_up, courier_in_transit, courier_delivered,
  // courier_failed, courier_rto, order_out_for_delivery, order_delivered).
  awb: [
    { value: 'awb.number', label: 'AWB Number (Tracking #)' },
    { value: 'awb.courier_name', label: 'Courier Name' },
    { value: 'awb.tracking_url', label: 'Courier Tracking URL' },
    { value: 'awb.tracking_link', label: 'Opswarex Tracking Link' },
  ],
  fulfillment: [
    { value: 'fulfillment.tracking_number', label: 'Tracking Number (AWB)' },
    { value: 'fulfillment.carrier', label: 'Carrier / Courier Name' },
    { value: 'fulfillment.tracking_url', label: 'Courier Tracking URL' },
  ],
  checkout: [
    { value: 'checkout.total_price', label: 'Cart Total' },
    { value: 'checkout.items_count', label: 'Cart Items' },
    { value: 'checkout.recovery_url', label: 'Recovery URL' },
    { value: 'checkout.first_item_name', label: 'First Item Name' },
  ],
  product: [
    { value: 'product.first_item_image', label: 'Product Image' },
    { value: 'product.first_item_name', label: 'Product Name' },
    { value: 'product.first_item_price', label: 'Product Price' },
  ],
  credit: [
    { value: 'credit.amount', label: 'Credit Amount' },
    { value: 'credit.balance_before', label: 'Balance Before' },
    { value: 'credit.balance_after', label: 'Balance After' },
    { value: 'credit.expires_at', label: 'Expiry Date' },
    { value: 'credit.days_until_expiry', label: 'Days Until Expiry' },
    { value: 'credit.reason', label: 'Reason' },
    { value: 'credit.credit_id', label: 'Credit ID' },
  ],
  store_credits: [
    // Surfaced on credit triggers (credits_added / credits_expiring /
    // credits_expired / credits_removed). Backend hydrates both `credit.*`
    // and `store_credits.*` namespaces in process_trigger so either works.
    { value: 'store_credits.credits_added', label: 'Credits Added' },
    { value: 'store_credits.credits_expiring', label: 'Credits Expiring Soon' },
    { value: 'store_credits.credits_expired', label: 'Credits Expired' },
    { value: 'store_credits.credits_removed', label: 'Credits Removed' },
    { value: 'store_credits.total_balance', label: 'Total Credit Balance' },
    { value: 'store_credits.currency', label: 'Credit Currency' },
    { value: 'store_credits.expiry_date', label: 'Credit Expiry Date' },
    { value: 'store_credits.days_until_expiry', label: 'Days Until Expiry' },
  ],
  shop: [
    { value: 'shop.name', label: 'Shop Name' },
    { value: 'shop.url', label: 'Shop URL' },
  ],
};

// Header media source options
const HEADER_MEDIA_SOURCES = [
  { value: 'static', label: 'Static URL', icon: Link },
  { value: 'upload', label: 'Upload File', icon: Upload },
  { value: 'product.first_item_image', label: 'Product Image (Dynamic)', icon: Image },
  { value: 'checkout.first_item_image', label: 'Cart Item Image (Dynamic)', icon: Image },
];

// Condition fields with their value type. Types drive the Value input renderer.
// type: 'number' | 'boolean' | 'text' | { enum: [{value,label}, ...] }
const CONDITION_FIELDS = [
  { value: 'order.total', label: 'Order Total', type: 'number' },
  { value: 'order.items_count', label: 'Items Count', type: 'number' },
  { value: 'order.is_cod', label: 'Order is COD (Cash on Delivery)', type: 'boolean' },
  { value: 'order.payment_method', label: 'Order Payment Method', type: { enum: [
    { value: 'cod', label: 'COD' },
    { value: 'cash_on_delivery', label: 'Cash on Delivery' },
    { value: 'card', label: 'Card' },
    { value: 'upi', label: 'UPI' },
    { value: 'netbanking', label: 'Net Banking' },
    { value: 'wallet', label: 'Wallet' },
    { value: 'store_credit', label: 'Store Credit' },
  ]}},
  { value: 'order.financial_status', label: 'Order Financial Status', type: { enum: [
    { value: 'pending', label: 'Pending' },
    { value: 'authorized', label: 'Authorized' },
    { value: 'partially_paid', label: 'Partially Paid' },
    { value: 'paid', label: 'Paid' },
    { value: 'partially_refunded', label: 'Partially Refunded' },
    { value: 'refunded', label: 'Refunded' },
    { value: 'voided', label: 'Voided' },
  ]}},
  { value: 'customer.orders_count', label: 'Customer Orders', type: 'number' },
  { value: 'customer.total_spent', label: 'Total Spent', type: 'number' },
  { value: 'checkout.total_price', label: 'Cart Value', type: 'number' },
  // Store credits
  { value: 'credit.amount', label: 'Credit Amount', type: 'number' },
  { value: 'credit.balance_after', label: 'Credit Balance After', type: 'number' },
  // Response-related fields (for Wait for Response node conditions)
  { value: 'response.received', label: 'Response Received', type: 'boolean' },
  { value: 'response.type', label: 'Response Type', type: { enum: [
    { value: 'location', label: 'Location' },
    { value: 'text', label: 'Text' },
    { value: 'image', label: 'Image' },
    { value: 'button_reply', label: 'Button Reply' },
    { value: 'any', label: 'Any' },
  ]}},
  { value: 'response.has_location', label: 'Has Location', type: 'boolean' },
  { value: 'response.button_id', label: 'Button Reply ID', type: 'text' },
  { value: 'response.button_text', label: 'Button Reply Text', type: 'text' },
  { value: 'response.text', label: 'Response Text', type: 'text' },
];

// Condition operators
const CONDITION_OPERATORS = [
  { value: '>', label: 'Greater than' },
  { value: '<', label: 'Less than' },
  { value: '>=', label: 'Greater or equal' },
  { value: '<=', label: 'Less or equal' },
  { value: '==', label: 'Equals' },
  { value: '!=', label: 'Not equals' },
  { value: 'contains', label: 'Contains' },
];

// Trigger types — MUST stay in sync with NodePalette.jsx and backend journey_module.TriggerType
const TRIGGER_TYPES = [
  // Orders / Checkout
  { value: 'checkout_abandoned', label: 'Checkout Abandoned' },
  { value: 'order_created', label: 'Order Created' },
  { value: 'order_paid', label: 'Order Paid' },
  { value: 'order_ready_for_dispatch', label: 'Ready For Dispatch' },
  { value: 'order_fulfilled', label: 'Order Fulfilled' },
  { value: 'order_shipped', label: 'Order Shipped' },
  { value: 'order_out_for_delivery', label: 'Out for Delivery' },
  { value: 'order_delivered', label: 'Order Delivered' },
  { value: 'order_delivery_failed', label: 'Order Delivery Failed' },
  { value: 'order_delivery_rescheduled', label: 'Delivery Rescheduled' },
  { value: 'return_accepted', label: 'Return Accepted' },
  { value: 'return_picked_up', label: 'Return Picked Up' },
  { value: 'return_qc_completed', label: 'Return QC Completed' },
  { value: 'return_refund_issued', label: 'Refund Issued' },
  { value: 'order_cancelled', label: 'Order Cancelled' },
  { value: 'order_refunded', label: 'Order Refunded' },
  // Payments
  { value: 'payment_received', label: 'Payment Received' },
  { value: 'payment_failed', label: 'Payment Failed' },
  // Customers / Leads
  { value: 'customer_created', label: 'Customer Created' },
  { value: 'lead_created', label: 'Lead Created' },
  // Courier / Fulfillment
  { value: 'awb_generated', label: 'AWB Generated' },
  { value: 'courier_assigned', label: 'Courier Assigned' },
  { value: 'courier_picked_up', label: 'Courier Picked Up' },
  { value: 'courier_in_transit', label: 'Courier In Transit' },
  { value: 'courier_delivered', label: 'Courier Delivered' },
  { value: 'courier_failed', label: 'Failed Delivery Attempt (NDR)' },
  { value: 'courier_rto', label: 'RTO (Return to Origin)' },
  // Production (made-to-order)
  { value: 'production_started', label: 'Production Started' },
  { value: 'production_eta_updated', label: 'Production ETA Updated' },
  { value: 'production_completed', label: 'Production Completed' },
  // Store Credits
  { value: 'credits_added', label: 'Credits Added' },
  { value: 'credits_expiring', label: 'Credits Expiring (Soon)' },
  { value: 'credits_expired', label: 'Credits Expired' },
  // Marketplace Vendor — Purchase Order lifecycle (Phase 3)
  { value: 'marketplace_po_created', label: 'MP PO · Created' },
  { value: 'marketplace_po_accepted', label: 'MP PO · Accepted by vendor' },
  { value: 'marketplace_po_rejected', label: 'MP PO · Rejected by vendor' },
  { value: 'marketplace_po_ready_for_pickup', label: 'MP PO · Ready for pickup' },
  { value: 'marketplace_po_picked_up', label: 'MP PO · Picked up by courier' },
  { value: 'marketplace_po_delivered', label: 'MP PO · Delivered' },
  // Marketplace Vendor Payouts (Phase 4)
  { value: 'marketplace_payout_submitted', label: 'Marketplace Payout · Submitted' },
  { value: 'marketplace_payout_approved', label: 'Marketplace Payout · Approved' },
  { value: 'marketplace_payout_paid', label: 'Marketplace Payout · Paid' },
  { value: 'marketplace_payout_rejected', label: 'Marketplace Payout · Rejected' },
  // Manual
  { value: 'manual', label: 'Manual Entry' },
];

// Exit condition types for trigger
const EXIT_CONDITION_TYPES = [
  { value: 'order_placed', label: 'Order Placed', description: 'Exit when customer places any order' },
  { value: 'checkout_started', label: 'Checkout Started', description: 'Exit when customer starts a new checkout (Shopify only)' },
  { value: 'checkout_recovered', label: 'Checkout Recovered', description: 'Exit when checkout is completed' },
  { value: 'order_shipped', label: 'Order Shipped', description: 'Exit when order ships' },
  { value: 'order_delivered', label: 'Order Delivered', description: 'Exit when order is delivered' },
  { value: 'order_cancelled', label: 'Order Cancelled', description: 'Exit when order is cancelled' },
  { value: 'unsubscribed', label: 'Customer Unsubscribed', description: 'Exit when customer opts out' },
  { value: 'replied', label: 'Customer Replied', description: 'Exit when customer sends a reply' },
];

function TriggerConfig({ node, onUpdate }) {
  const [showExitConditions, setShowExitConditions] = useState(
    (node.data.exit_triggers?.length > 0) || false
  );
  
  const toggleExitCondition = (condType) => {
    const currentExits = node.data.exit_triggers || [];
    const exists = currentExits.some(e => e.type === condType);
    
    if (exists) {
      onUpdate(node.id, { 
        exit_triggers: currentExits.filter(e => e.type !== condType) 
      });
    } else {
      onUpdate(node.id, { 
        exit_triggers: [...currentExits, { type: condType }] 
      });
    }
  };
  
  const isExitEnabled = (condType) => {
    return (node.data.exit_triggers || []).some(e => e.type === condType);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-wt-text-primary">Trigger Type</Label>
        <Select
          value={node.data.trigger_type || 'checkout_abandoned'}
          onValueChange={(v) => onUpdate(node.id, { trigger_type: v })}
        >
          <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            {TRIGGER_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value} className="text-wt-text-primary">
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
        <p className="text-xs text-green-400">
          This journey will start when the selected trigger event occurs.
        </p>
      </div>
      
      {/* Exit Triggers Section */}
      <div className="border-t border-wt-border pt-4">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setShowExitConditions(!showExitConditions)}
        >
          <div>
            <Label className="text-wt-text-primary cursor-pointer">Exit Triggers</Label>
            <p className="text-xs text-wt-text-muted">Stop journey when conditions are met</p>
          </div>
          <div className="flex items-center gap-2">
            {(node.data.exit_triggers?.length > 0) && (
              <span className="text-xs text-amber-400 bg-amber-500/20 px-2 py-0.5 rounded">
                {node.data.exit_triggers.length} active
              </span>
            )}
            <span className="text-wt-text-muted text-sm">
              {showExitConditions ? '▼' : '▶'}
            </span>
          </div>
        </div>
        
        {showExitConditions && (
          <div className="mt-3 space-y-2">
            {EXIT_CONDITION_TYPES.map(cond => (
              <div 
                key={cond.value}
                onClick={() => toggleExitCondition(cond.value)}
                className={`
                  p-3 rounded-lg border cursor-pointer transition-all
                  ${isExitEnabled(cond.value) 
                    ? 'bg-amber-500/10 border-amber-500/50' 
                    : 'bg-wt-paper border-wt-border hover:border-wt-gold/30'
                  }
                `}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${isExitEnabled(cond.value) ? 'text-amber-400' : 'text-wt-text-primary'}`}>
                      {cond.label}
                    </p>
                    <p className="text-xs text-wt-text-muted">{cond.description}</p>
                  </div>
                  <div className={`
                    w-5 h-5 rounded border-2 flex items-center justify-center
                    ${isExitEnabled(cond.value) 
                      ? 'bg-amber-500 border-amber-500' 
                      : 'border-wt-border'
                    }
                  `}>
                    {isExitEnabled(cond.value) && (
                      <span className="text-black text-xs">✓</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30 mt-3">
              <p className="text-xs text-amber-400">
                Exit triggers are checked before each step. If any condition is met, the customer will exit immediately.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ActionConfig({ node, onUpdate, templates }) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Find selected template
  useEffect(() => {
    if (node.data.template_name && templates) {
      const template = templates.find(t => t.name === node.data.template_name);
      setSelectedTemplate(template);
    }
  }, [node.data.template_name, templates]);
  
  // Count variables in template body
  const getVariableCount = (template) => {
    if (!template?.components) return 0;
    const bodyComponent = template.components.find(c => c.type === 'BODY');
    if (!bodyComponent?.text) return 0;
    const matches = bodyComponent.text.match(/\{\{(\d+)\}\}/g);
    return matches ? matches.length : 0;
  };
  
  // Check if template has buttons with dynamic content
  const getButtonsWithVariables = (template) => {
    if (!template?.components) return [];
    const buttonsComponent = template.components.find(c => c.type === 'BUTTONS');
    if (!buttonsComponent?.buttons) return [];
    
    // Return ALL buttons for configuration
    return buttonsComponent.buttons.map((btn, index) => ({
      index,
      type: btn.type,
      text: btn.text,
      url: btn.url,
      example: btn.example,
      phone_number: btn.phone_number,
      // Check if URL has variable {{1}}
      hasVariable: btn.url?.includes('{{') || false,
      // Extract base URL (before the variable part)
      baseUrl: btn.url ? btn.url.split('{{')[0] : ''
    }));
  };
  
  // Check if template has media header
  const hasMediaHeader = (template) => {
    if (!template?.components) return false;
    return template.components.some(c => 
      c.type === 'HEADER' && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(c.format)
    );
  };
  
  // Get header format type
  const getHeaderFormat = (template) => {
    if (!template?.components) return null;
    const header = template.components.find(c => c.type === 'HEADER');
    return header?.format;
  };
  
  // Handle file upload for header media
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type based on header format
    const headerFormat = getHeaderFormat(selectedTemplate);
    const allowedTypes = {
      IMAGE: ['image/jpeg', 'image/png', 'image/webp'],
      VIDEO: ['video/mp4', 'video/3gpp'],
      DOCUMENT: ['application/pdf'],
    };
    
    if (headerFormat && allowedTypes[headerFormat] && !allowedTypes[headerFormat].includes(file.type)) {
      toast.error(`Invalid file type. Allowed: ${allowedTypes[headerFormat].join(', ')}`);
      return;
    }
    
    // Max file size (16MB for videos, 5MB for images)
    const maxSize = headerFormat === 'VIDEO' ? 16 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Max: ${maxSize / 1024 / 1024}MB`);
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_BASE_URL}/storage/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      const uploadedUrl = response.data.url;
      onUpdate(node.id, { 
        header_media_url: uploadedUrl,
        header_media_source: 'static',
      });
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };
  
  const variableCount = selectedTemplate ? getVariableCount(selectedTemplate) : 0;
  const templateHasMediaHeader = selectedTemplate ? hasMediaHeader(selectedTemplate) : false;
  const headerFormat = selectedTemplate ? getHeaderFormat(selectedTemplate) : null;
  const buttonsWithVariables = selectedTemplate ? getButtonsWithVariables(selectedTemplate) : [];
  
  return (
    <div className="space-y-4">
      {/* Action Type: Email */}
      {(node.data.action_type === 'email') ? (
        <EmailActionConfig node={node} onUpdate={onUpdate} />
      ) : (node.data.action_type === 'push') ? (
        <PushActionConfig node={node} onUpdate={onUpdate} />
      ) : (node.data.action_type === 'add_tag' || node.data.action_type === 'remove_tag') ? (
        <TagActionConfig node={node} onUpdate={onUpdate} />
      ) : (node.data.action_type === 'cancel_order') ? (
        <CancelOrderConfig node={node} onUpdate={onUpdate} />
      ) : (node.data.action_type === 'add_store_credit' || node.data.action_type === 'remove_store_credit') ? (
        <StoreCreditConfig node={node} onUpdate={onUpdate} />
      ) : (node.data.action_type === 'update_customer_tag') ? (
        <CustomerTagConfig node={node} onUpdate={onUpdate} />
      ) : (node.data.action_type === 'update_order_note') ? (
        <OrderNoteConfig node={node} onUpdate={onUpdate} />
      ) : (node.data.action_type === 'http_webhook') ? (
        <HttpWebhookConfig node={node} onUpdate={onUpdate} />
      ) : (node.data.action_type === 'create_task') ? (
        <CreateTaskConfig node={node} onUpdate={onUpdate} />
      ) : (node.data.action_type === 'send_slack' || node.data.action_type === 'send_discord') ? (
        <SimpleWebhookConfig node={node} onUpdate={onUpdate} />
      ) : (
      <>
      <div>
        <Label className="text-wt-text-primary">WhatsApp Template</Label>
        <Select
          value={node.data.template_name || ''}
          onValueChange={(v) => {
            const template = templates?.find(t => t.name === v);
            // Extract quick-reply button labels so a downstream Button Router
            // node can auto-detect them without needing the full template list.
            const buttonsComp = template?.components?.find(c => c.type === 'BUTTONS');
            const qr_buttons = (buttonsComp?.buttons || [])
              .filter(b => b.type === 'QUICK_REPLY' && b.text)
              .map(b => b.text);
            onUpdate(node.id, { 
              template_name: v,
              template_variables: {},
              template_qr_buttons: qr_buttons,
            });
            setSelectedTemplate(template);
          }}
        >
          <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
            <SelectValue placeholder="Select template" />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border max-h-64">
            {templates?.filter(t => t.status === 'APPROVED').map(template => (
              <SelectItem 
                key={template.name} 
                value={template.name}
                className="text-wt-text-primary"
              >
                {template.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Template Preview */}
      {selectedTemplate && (
        <div className="p-3 bg-wt-bg rounded-lg border border-wt-border">
          <p className="text-xs text-wt-text-muted mb-1">Template Preview:</p>
          <p className="text-sm text-wt-text-primary whitespace-pre-wrap">
            {(() => {
              let previewText = selectedTemplate.components?.find(c => c.type === 'BODY')?.text || 'No body text';
              // Replace variables with mapped values or fallbacks
              const templateVars = node.data.template_variables || {};
              for (let i = 1; i <= 10; i++) {
                const varKey = `var_${i}`;
                const fallbackKey = `fallback_${i}`;
                const mappedField = templateVars[varKey];
                const fallbackValue = templateVars[fallbackKey];
                
                // Find the display label for the mapped field
                let displayValue = `{{${i}}}`;
                if (mappedField) {
                  // Find label from VARIABLE_FIELDS
                  for (const fields of Object.values(VARIABLE_FIELDS)) {
                    const field = fields.find(f => f.value === mappedField);
                    if (field) {
                      displayValue = `[${field.label}${fallbackValue ? ` | "${fallbackValue}"` : ''}]`;
                      break;
                    }
                  }
                } else if (fallbackValue) {
                  displayValue = `[${fallbackValue}]`;
                }
                
                previewText = previewText.replace(new RegExp(`\\{\\{${i}\\}\\}`, 'g'), displayValue);
              }
              return previewText;
            })()}
          </p>
        </div>
      )}
      
      {/* Variable Mappings with Fallback */}
      {variableCount > 0 && (
        <div className="space-y-3">
          <Label className="text-wt-text-primary">Variable Mappings</Label>
          {Array.from({ length: variableCount }, (_, i) => (
            <div key={i} className="p-3 bg-wt-paper rounded-lg border border-wt-border space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-wt-gold min-w-[40px] font-mono">{`{{${i + 1}}}`}</span>
                <Select
                  value={node.data.template_variables?.[`var_${i + 1}`] || ''}
                  onValueChange={(v) => {
                    const newVars = { ...node.data.template_variables, [`var_${i + 1}`]: v };
                    onUpdate(node.id, { template_variables: newVars });
                  }}
                >
                  <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary flex-1">
                    <SelectValue placeholder="Select field" />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border max-h-64">
                    {Object.entries(VARIABLE_FIELDS).map(([group, fields]) => (
                      <div key={group}>
                        <div className="px-2 py-1 text-xs font-medium text-wt-text-muted uppercase">
                          {group}
                        </div>
                        {fields.map(field => (
                          <SelectItem 
                            key={field.value} 
                            value={field.value}
                            className="text-wt-text-primary"
                          >
                            {field.label}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Fallback Value Input */}
              <div className="pl-12">
                <Input
                  value={node.data.template_variables?.[`fallback_${i + 1}`] || ''}
                  onChange={(e) => {
                    const newVars = { ...node.data.template_variables, [`fallback_${i + 1}`]: e.target.value };
                    onUpdate(node.id, { template_variables: newVars });
                  }}
                  placeholder="Fallback value (if field is empty)"
                  className="bg-wt-bg border-wt-border text-wt-text-primary text-sm h-8"
                />
                <p className="text-[10px] text-wt-text-muted mt-1">
                  Used when the selected field has no value
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Button Variables for URL/Dynamic Buttons */}
      {buttonsWithVariables.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-wt-text-primary">Button Configuration</Label>
            <span className="text-[10px] text-wt-text-muted bg-wt-bg px-2 py-0.5 rounded">
              {buttonsWithVariables.length} button{buttonsWithVariables.length > 1 ? 's' : ''}
            </span>
          </div>
          <p className="text-xs text-wt-text-muted -mt-2">
            Configure button values. Note: Button text is fixed by WhatsApp template approval.
          </p>
          {buttonsWithVariables.map((btn, btnIndex) => (
            <div key={btnIndex} className="p-3 bg-wt-paper rounded-lg border border-wt-border space-y-3">
              {/* Button Header */}
              <div className="flex items-center justify-between pb-2 border-b border-wt-border/50">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-wt-text-muted">Button {btnIndex + 1}</span>
                  <span className="text-xs px-2 py-0.5 bg-wt-gold/20 text-wt-gold rounded">
                    {btn.type}
                  </span>
                </div>
              </div>
              
              {/* Button Text - Read Only */}
              <div className="space-y-1">
                <Label className="text-xs text-wt-text-muted">Button Text (Fixed)</Label>
                <div className="px-3 py-2 bg-wt-bg rounded border border-wt-border/50 text-sm text-wt-text-primary">
                  {btn.text || 'No text'}
                </div>
                <p className="text-[10px] text-wt-text-muted">Button text cannot be changed - it's set in the approved template</p>
              </div>
              
              {/* URL Button Configuration */}
              {btn.type === 'URL' && (
                <div className="space-y-3">
                  {btn.hasVariable ? (
                    <>
                      {/* Dynamic URL - has variable placeholder */}
                      <div className="p-2 bg-green-500/10 rounded border border-green-500/30">
                        <p className="text-xs text-green-400">✓ This button has a dynamic URL</p>
                        <p className="text-[10px] text-wt-text-muted mt-1">Base: {btn.baseUrl}</p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-xs text-wt-text-muted">URL Value Source</Label>
                        <Select
                          value={node.data.button_config?.[`btn_${btnIndex}_url_source`] || 'static'}
                          onValueChange={(v) => {
                            const newConfig = { ...node.data.button_config, [`btn_${btnIndex}_url_source`]: v };
                            onUpdate(node.id, { button_config: newConfig });
                          }}
                        >
                          <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                            <SelectValue placeholder="Select value source" />
                          </SelectTrigger>
                          <SelectContent className="bg-wt-surface border-wt-border max-h-64">
                            <SelectItem value="static" className="text-wt-text-primary">
                              Enter Custom Value
                            </SelectItem>
                            {Object.entries(VARIABLE_FIELDS).map(([group, fields]) => (
                              <div key={group}>
                                <div className="px-2 py-1 text-xs font-medium text-wt-text-muted uppercase">
                                  {group}
                                </div>
                                {fields.map(field => (
                                  <SelectItem 
                                    key={field.value} 
                                    value={field.value}
                                    className="text-wt-text-primary"
                                  >
                                    {field.label}
                                  </SelectItem>
                                ))}
                              </div>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Custom URL Value Input */}
                      {(node.data.button_config?.[`btn_${btnIndex}_url_source`] === 'static' || 
                        !node.data.button_config?.[`btn_${btnIndex}_url_source`]) && (
                        <div className="space-y-1">
                          <Label className="text-xs text-wt-text-muted">URL Suffix Value</Label>
                          <Input
                            value={node.data.button_config?.[`btn_${btnIndex}_url_value`] || ''}
                            onChange={(e) => {
                              const newConfig = { ...node.data.button_config, [`btn_${btnIndex}_url_value`]: e.target.value };
                              onUpdate(node.id, { button_config: newConfig });
                            }}
                            placeholder="e.g., /product/SKU123 or tracking-id"
                            className="bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                          />
                        </div>
                      )}
                      
                      {/* Fallback for dynamic field */}
                      {node.data.button_config?.[`btn_${btnIndex}_url_source`] && 
                       node.data.button_config?.[`btn_${btnIndex}_url_source`] !== 'static' && (
                        <div className="space-y-1">
                          <Label className="text-xs text-wt-text-muted">Fallback Value (if field is empty)</Label>
                          <Input
                            value={node.data.button_config?.[`btn_${btnIndex}_url_fallback`] || ''}
                            onChange={(e) => {
                              const newConfig = { ...node.data.button_config, [`btn_${btnIndex}_url_fallback`]: e.target.value };
                              onUpdate(node.id, { button_config: newConfig });
                            }}
                            placeholder="Default URL suffix if dynamic value is empty"
                            className="bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Static URL - no variable in template */}
                      <div className="p-2 bg-yellow-500/10 rounded border border-yellow-500/30">
                        <p className="text-xs text-yellow-400">Static URL (cannot be changed)</p>
                        <p className="text-[10px] text-wt-text-muted mt-1 font-mono break-all">{btn.url || 'Not set'}</p>
                      </div>
                      <p className="text-[10px] text-wt-text-muted">
                        To use dynamic URLs, recreate the template with {"{{1}}"} placeholder in the URL.
                      </p>
                    </>
                  )}
                </div>
              )}
              
              {/* Phone Number Button - Always Fixed */}
              {btn.type === 'PHONE_NUMBER' && (
                <div className="space-y-2">
                  <Label className="text-xs text-wt-text-muted">Phone Number (Fixed)</Label>
                  <div className="px-3 py-2 bg-wt-bg rounded border border-wt-border/50 text-sm text-wt-text-primary font-mono">
                    {btn.phone_number || 'Not set'}
                  </div>
                  <p className="text-[10px] text-wt-text-muted">
                    Phone number is fixed in the template. Clicking opens the dialer.
                  </p>
                </div>
              )}
              
              {/* Quick Reply Button */}
              {btn.type === 'QUICK_REPLY' && (
                <div className="space-y-2">
                  <p className="text-xs text-wt-text-muted">
                    When customer clicks, they'll send "{btn.text}" as a reply.
                  </p>
                  <div className="space-y-1">
                    <Label className="text-xs text-wt-text-muted">Action on Click</Label>
                    <Select
                      value={node.data.button_config?.[`btn_${btnIndex}_action`] || 'none'}
                      onValueChange={(v) => {
                        const newConfig = { ...node.data.button_config, [`btn_${btnIndex}_action`]: v };
                        onUpdate(node.id, { button_config: newConfig });
                      }}
                    >
                      <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-surface border-wt-border">
                        <SelectItem value="none" className="text-wt-text-primary">Continue Journey</SelectItem>
                        <SelectItem value="exit_journey" className="text-wt-text-primary">Exit Journey</SelectItem>
                        <SelectItem value="mark_interested" className="text-wt-text-primary">Mark as Interested</SelectItem>
                        <SelectItem value="mark_not_interested" className="text-wt-text-primary">Mark as Not Interested</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              
              {/* Copy Code Button */}
              {btn.type === 'COPY_CODE' && (
                <div className="space-y-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-wt-text-muted">Code/Coupon Value</Label>
                    <Select
                      value={node.data.button_config?.[`btn_${btnIndex}_code_source`] || 'static'}
                      onValueChange={(v) => {
                        const newConfig = { ...node.data.button_config, [`btn_${btnIndex}_code_source`]: v };
                        onUpdate(node.id, { button_config: newConfig });
                      }}
                    >
                      <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                        <SelectValue placeholder="Select code source" />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-surface border-wt-border">
                        <SelectItem value="static" className="text-wt-text-primary">Enter Custom Code</SelectItem>
                        <SelectItem value="order.discount_code" className="text-wt-text-primary">Order Discount Code</SelectItem>
                        <SelectItem value="customer.loyalty_code" className="text-wt-text-primary">Customer Loyalty Code</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {(node.data.button_config?.[`btn_${btnIndex}_code_source`] === 'static' || 
                    !node.data.button_config?.[`btn_${btnIndex}_code_source`]) && (
                    <Input
                      value={node.data.button_config?.[`btn_${btnIndex}_code`] || ''}
                      onChange={(e) => {
                        const newConfig = { ...node.data.button_config, [`btn_${btnIndex}_code`]: e.target.value };
                        onUpdate(node.id, { button_config: newConfig });
                      }}
                      placeholder="Enter coupon code (e.g., WELCOME20)"
                      className="bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                    />
                  )}
                  <p className="text-[10px] text-wt-text-muted">
                    Customer can tap to copy this code to clipboard.
                  </p>
                </div>
              )}
              
              {/* Flow Button */}
              {btn.type === 'FLOW' && (
                <div className="p-2 bg-blue-500/10 rounded border border-blue-500/30">
                  <p className="text-xs text-blue-400">WhatsApp Flow Button</p>
                  <p className="text-[10px] text-wt-text-muted mt-1">
                    Flow configuration is managed in WhatsApp Business Manager.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Header Media Source */}
      {templateHasMediaHeader && (
        <div className="space-y-3 p-3 bg-wt-paper rounded-lg border border-wt-border">
          <div className="flex items-center gap-2">
            <Image className="h-4 w-4 text-wt-gold" />
            <Label className="text-wt-text-primary">Header Media ({headerFormat})</Label>
          </div>
          
          <Select
            value={node.data.header_media_source || 'product.first_item_image'}
            onValueChange={(v) => onUpdate(node.id, { header_media_source: v })}
          >
            <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-wt-surface border-wt-border">
              {HEADER_MEDIA_SOURCES.map(source => (
                <SelectItem key={source.value} value={source.value} className="text-wt-text-primary">
                  <div className="flex items-center gap-2">
                    <source.icon className="h-3 w-3" />
                    {source.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Static URL Input */}
          {node.data.header_media_source === 'static' && (
            <Input
              value={node.data.header_media_url || ''}
              onChange={(e) => onUpdate(node.id, { header_media_url: e.target.value })}
              placeholder="https://example.com/image.jpg"
              className="bg-wt-bg border-wt-border text-wt-text-primary"
            />
          )}
          
          {/* File Upload */}
          {node.data.header_media_source === 'upload' && (
            <div className="space-y-2">
              <input
                type="file"
                id="header-media-upload"
                className="hidden"
                accept={
                  headerFormat === 'IMAGE' ? 'image/jpeg,image/png,image/webp' :
                  headerFormat === 'VIDEO' ? 'video/mp4,video/3gpp' :
                  headerFormat === 'DOCUMENT' ? 'application/pdf' :
                  '*'
                }
                onChange={handleFileUpload}
              />
              <label
                htmlFor="header-media-upload"
                className={`
                  flex items-center justify-center gap-2 p-3 
                  border-2 border-dashed border-wt-border rounded-lg
                  cursor-pointer hover:border-wt-gold hover:bg-wt-gold/5
                  transition-colors
                  ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                <Upload className="h-4 w-4 text-wt-text-muted" />
                <span className="text-sm text-wt-text-muted">
                  {uploading ? 'Uploading...' : 'Click to upload file'}
                </span>
              </label>
              
              {node.data.header_media_url && (
                <div className="flex items-center gap-2 p-2 bg-wt-bg rounded text-xs text-wt-text-muted">
                  <Image className="h-3 w-3" />
                  <span className="truncate flex-1">{node.data.header_media_url}</span>
                </div>
              )}
              
              <p className="text-[10px] text-wt-text-muted">
                {headerFormat === 'IMAGE' && 'Supported: JPEG, PNG, WebP (max 5MB)'}
                {headerFormat === 'VIDEO' && 'Supported: MP4, 3GPP (max 16MB)'}
                {headerFormat === 'DOCUMENT' && 'Supported: PDF (max 5MB)'}
              </p>
            </div>
          )}
          
          {/* Dynamic source info */}
          {node.data.header_media_source && !['static', 'upload'].includes(node.data.header_media_source) && (
            <p className="text-xs text-wt-text-muted">
              The image will be dynamically loaded from the {node.data.header_media_source.replace('.', ' ')} when the journey runs.
            </p>
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
}

function EmailActionConfig({ node, onUpdate }) {
  const VARIABLE_HINTS = [
    '{customer_name}', '{customer_email}', '{customer_phone}',
    '{order_number}', '{order_total}', '{order_status}',
    '{awb_number}', '{courier_type}', '{courier_status}',
    '{tracking_link}', '{estimated_delivery}',
    '{company_name}', '{order_id}'
  ];

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-wt-text-primary">Email Subject</Label>
        <Input
          value={node.data.email_subject || ''}
          onChange={e => onUpdate(node.id, { email_subject: e.target.value })}
          placeholder="e.g. Order Confirmation - {order_number}"
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
          data-testid="email-subject-config"
        />
      </div>

      <div>
        <Label className="text-wt-text-primary">Email Body (HTML)</Label>
        <Textarea
          value={node.data.email_body_html || ''}
          onChange={e => onUpdate(node.id, { email_body_html: e.target.value })}
          placeholder="<p>Hi {customer_name},</p><p>Your order {order_number} has been confirmed.</p>"
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1 min-h-[120px] font-mono text-sm"
          data-testid="email-body-config"
        />
      </div>

      <div>
        <Label className="text-wt-text-primary">Plain Text Body (fallback)</Label>
        <Textarea
          value={node.data.email_body_text || ''}
          onChange={e => onUpdate(node.id, { email_body_text: e.target.value })}
          placeholder="Hi {customer_name}, Your order {order_number} has been confirmed."
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1 min-h-[80px]"
        />
      </div>

      <div>
        <Label className="text-wt-text-primary text-xs">Available Variables</Label>
        <div className="flex flex-wrap gap-1 mt-1">
          {VARIABLE_HINTS.map(v => (
            <Badge key={v} className="bg-blue-500/20 text-blue-400 cursor-pointer text-xs"
              onClick={() => {
                navigator.clipboard.writeText(v);
              }}
            >{v}</Badge>
          ))}
        </div>
        <p className="text-xs text-wt-text-muted mt-1">Click to copy. Variables are replaced with actual values at send time.</p>
      </div>
    </div>
  );
}

/**
 * Push Notification action config — title / body / image / CTA.
 * The journey executor (`execute_push_node` in journey_module.py)
 * resolves all of the customer's active subscriptions and pushes to
 * each device. Skips gracefully if the customer has no subscribers.
 */
function PushActionConfig({ node, onUpdate }) {
  const set = (k, v) => onUpdate(node.id, { [k]: v });
  return (
    <div className="space-y-4" data-testid="push-action-config">
      <div>
        <Label className="text-wt-text-primary">Title</Label>
        <Input
          value={node.data.push_title || ''}
          onChange={e => set('push_title', e.target.value)}
          placeholder="Your order is on its way 🚚"
          maxLength={80}
          className="bg-wt-bg border-wt-border text-wt-text-primary"
          data-testid="push-action-title"
        />
        <p className="text-[10px] text-wt-text-muted text-right">{(node.data.push_title || '').length}/80</p>
      </div>
      <div>
        <Label className="text-wt-text-primary">Body</Label>
        <Textarea
          rows={3} maxLength={240}
          value={node.data.push_body || ''}
          onChange={e => set('push_body', e.target.value)}
          placeholder="Hi {{first_name}}, your order #{{order_number}} just shipped."
          className="bg-wt-bg border-wt-border text-wt-text-primary"
          data-testid="push-action-body"
        />
        <p className="text-[10px] text-wt-text-muted text-right">{(node.data.push_body || '').length}/240</p>
      </div>
      <div>
        <Label className="text-wt-text-primary">Image URL (optional)</Label>
        <Input
          value={node.data.push_image_url || ''}
          onChange={e => set('push_image_url', e.target.value)}
          placeholder="https://cdn.shop.com/banner.jpg"
          className="bg-wt-bg border-wt-border text-wt-text-primary"
          data-testid="push-action-image"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-wt-text-primary">Click URL</Label>
          <Input
            value={node.data.push_cta_url || ''}
            onChange={e => set('push_cta_url', e.target.value)}
            placeholder="https://shop.com/order/{{order_number}}"
            className="bg-wt-bg border-wt-border text-wt-text-primary"
            data-testid="push-action-cta-url"
          />
        </div>
        <div>
          <Label className="text-wt-text-primary">Button label</Label>
          <Input
            value={node.data.push_cta_label || ''}
            onChange={e => set('push_cta_label', e.target.value)}
            placeholder="Track order"
            className="bg-wt-bg border-wt-border text-wt-text-primary"
            data-testid="push-action-cta-label"
          />
        </div>
      </div>
      <p className="text-xs text-wt-text-muted">
        We push to every browser the customer has subscribed (multi-device fan-out). A {`?utm_source=push&utm_campaign=<journey>`} is appended to the click URL automatically.
      </p>
    </div>
  );
}

function TagActionConfig({ node, onUpdate }) {
  const isAdd = node.data.action_type === 'add_tag';
  return (
    <div className="space-y-4">
      <div className={`p-3 rounded-lg border ${isAdd ? 'bg-teal-500/10 border-teal-500/30' : 'bg-orange-500/10 border-orange-500/30'}`}>
        <p className={`text-xs ${isAdd ? 'text-teal-400' : 'text-orange-400'}`}>
          {isAdd
            ? 'Adds the tag to this order. If linked to Shopify, the tag will also be pushed to the Shopify order.'
            : 'Removes the tag from this order (and from Shopify if linked).'}
        </p>
      </div>
      <div>
        <Label className="text-wt-text-primary">Tag Name</Label>
        <Input
          value={node.data.tag_value || ''}
          onChange={(e) => onUpdate(node.id, { tag_value: e.target.value })}
          placeholder="e.g., cod_confirmed"
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
        <p className="text-xs text-wt-text-muted mt-1">Tags are matched case-insensitively.</p>
      </div>
      <div className="flex items-center justify-between p-3 bg-wt-paper rounded-lg border border-wt-border">
        <div>
          <Label className="text-wt-text-primary">Sync to Shopify</Label>
          <p className="text-xs text-wt-text-muted">Push the updated tag list to the Shopify order</p>
        </div>
        <input
          type="checkbox"
          checked={node.data.sync_to_shopify !== false}
          onChange={(e) => onUpdate(node.id, { sync_to_shopify: e.target.checked })}
          className="h-4 w-4 accent-wt-gold"
        />
      </div>
      <div>
        <Label className="text-wt-text-primary">Node Label</Label>
        <Input
          value={node.data.label || ''}
          onChange={(e) => onUpdate(node.id, { label: e.target.value })}
          placeholder={isAdd ? 'e.g., Tag: cod_confirmed' : 'e.g., Remove cod_pending'}
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
      </div>
    </div>
  );
}

function CancelOrderConfig({ node, onUpdate }) {
  return (
    <div className="space-y-4">
      <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
        <p className="text-xs text-red-400">
          Cancels the order in our system. If "Sync to Shopify" is enabled, the cancellation is also pushed to Shopify using the configured reason/restock/notify options.
        </p>
      </div>
      <div>
        <Label className="text-wt-text-primary">Cancel Reason</Label>
        <Select
          value={node.data.cancel_reason || 'customer'}
          onValueChange={(v) => onUpdate(node.id, { cancel_reason: v })}
        >
          <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="customer" className="text-wt-text-primary">Customer request</SelectItem>
            <SelectItem value="inventory" className="text-wt-text-primary">Out of stock</SelectItem>
            <SelectItem value="fraud" className="text-wt-text-primary">Fraud</SelectItem>
            <SelectItem value="declined" className="text-wt-text-primary">Payment declined</SelectItem>
            <SelectItem value="other" className="text-wt-text-primary">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between p-3 bg-wt-paper rounded-lg border border-wt-border">
        <div>
          <Label className="text-wt-text-primary">Restock Inventory</Label>
          <p className="text-xs text-wt-text-muted">Return items to stock on cancel</p>
        </div>
        <input
          type="checkbox"
          checked={node.data.restock !== false}
          onChange={(e) => onUpdate(node.id, { restock: e.target.checked })}
          className="h-4 w-4 accent-wt-gold"
        />
      </div>
      <div className="flex items-center justify-between p-3 bg-wt-paper rounded-lg border border-wt-border">
        <div>
          <Label className="text-wt-text-primary">Notify Customer (via Shopify)</Label>
          <p className="text-xs text-wt-text-muted">Shopify will email the customer about the cancellation</p>
        </div>
        <input
          type="checkbox"
          checked={!!node.data.notify_customer}
          onChange={(e) => onUpdate(node.id, { notify_customer: e.target.checked })}
          className="h-4 w-4 accent-wt-gold"
        />
      </div>
      <div className="flex items-center justify-between p-3 bg-wt-paper rounded-lg border border-wt-border">
        <div>
          <Label className="text-wt-text-primary">Sync to Shopify</Label>
          <p className="text-xs text-wt-text-muted">Cancel the order in Shopify too</p>
        </div>
        <input
          type="checkbox"
          checked={node.data.sync_to_shopify !== false}
          onChange={(e) => onUpdate(node.id, { sync_to_shopify: e.target.checked })}
          className="h-4 w-4 accent-wt-gold"
        />
      </div>
      <div>
        <Label className="text-wt-text-primary">Node Label</Label>
        <Input
          value={node.data.label || ''}
          onChange={(e) => onUpdate(node.id, { label: e.target.value })}
          placeholder="e.g., Cancel Order (COD declined)"
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
      </div>
    </div>
  );
}


function StoreCreditConfig({ node, onUpdate }) {
  const isAdd = node.data.action_type === 'add_store_credit';
  const amountType = node.data.credit_amount_type || 'fixed';
  return (
    <div className="space-y-4">
      {isAdd && (
        <div>
          <Label className="text-wt-text-primary">Credit Type</Label>
          <Select
            value={amountType}
            onValueChange={(v) => onUpdate(node.id, { credit_amount_type: v })}
          >
            <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1" data-testid="credit-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fixed">Fixed Amount</SelectItem>
              <SelectItem value="percentage">% of Order Total (Cashback)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {isAdd && amountType === 'percentage' ? (
        <>
          <div>
            <Label className="text-wt-text-primary">Cashback Percentage (%)</Label>
            <Input
              type="number"
              step="0.01"
              value={node.data.credit_percentage || ''}
              onChange={(e) => onUpdate(node.id, { credit_percentage: parseFloat(e.target.value) || 0 })}
              placeholder="e.g., 5 (for 5% of order total)"
              className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
              data-testid="credit-percentage-input"
            />
            <p className="text-xs text-wt-text-muted mt-1">
              Customer receives this percentage of the order total as cashback.
            </p>
          </div>
          <div>
            <Label className="text-wt-text-primary">Base Field (optional)</Label>
            <Input
              value={node.data.credit_base || 'order.total'}
              onChange={(e) => onUpdate(node.id, { credit_base: e.target.value })}
              placeholder="order.total"
              className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
            />
            <p className="text-xs text-wt-text-muted mt-1">
              Defaults to order total. Use <code className="text-wt-gold">{'{{order.subtotal}}'}</code> or a custom field path.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-wt-text-primary">Min Credit (optional)</Label>
              <Input
                type="number"
                value={node.data.credit_min_amount || ''}
                onChange={(e) => onUpdate(node.id, { credit_min_amount: parseFloat(e.target.value) || 0 })}
                placeholder="e.g., 50"
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
              />
            </div>
            <div>
              <Label className="text-wt-text-primary">Max Credit (optional)</Label>
              <Input
                type="number"
                value={node.data.credit_max_amount || ''}
                onChange={(e) => onUpdate(node.id, { credit_max_amount: parseFloat(e.target.value) || 0 })}
                placeholder="e.g., 500"
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
              />
            </div>
          </div>
        </>
      ) : (
        <div>
          <Label className="text-wt-text-primary">Amount</Label>
          <Input
            type="number"
            value={node.data.credit_amount || ''}
            onChange={(e) => onUpdate(node.id, { credit_amount: parseFloat(e.target.value) || 0 })}
            placeholder="e.g., 100"
            className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
          />
          <p className="text-xs text-wt-text-muted mt-1">
            You can also use a variable like <code className="text-wt-gold">{'{{order.total}}'}</code> in the reason to reference dynamic values.
          </p>
        </div>
      )}

      {isAdd && (
        <div>
          <Label className="text-wt-text-primary">Expires in (days, optional)</Label>
          <Input
            type="number"
            value={node.data.credit_expires_in_days || ''}
            onChange={(e) => onUpdate(node.id, { credit_expires_in_days: parseInt(e.target.value, 10) || 0 })}
            placeholder="e.g., 90 (leave empty for never)"
            className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
          />
        </div>
      )}
      <div>
        <Label className="text-wt-text-primary">Reason</Label>
        <Input
          value={node.data.credit_reason || ''}
          onChange={(e) => onUpdate(node.id, { credit_reason: e.target.value })}
          placeholder={isAdd ? 'e.g., Welcome bonus' : 'e.g., Refund for order {{order.number}}'}
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
      </div>
      <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
        <p className="text-xs text-purple-300">
          {isAdd
            ? 'Adds store credit to the customer\'s balance. Also pushes to Shopify\'s native Store Credit Account if the customer is linked (requires write_store_credit_account_transactions scope). Downstream nodes can reference {{credit.amount}}, {{credit.balance_after}}, {{credit.expires_at}}.'
            : 'Deducts credit from the customer\'s balance. Skipped gracefully if insufficient balance.'}
        </p>
      </div>
    </div>
  );
}

function CustomerTagConfig({ node, onUpdate }) {
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-wt-text-primary">Operation</Label>
        <Select
          value={node.data.tag_op || 'add'}
          onValueChange={(v) => onUpdate(node.id, { tag_op: v })}
        >
          <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="add" className="text-wt-text-primary">Add Tag</SelectItem>
            <SelectItem value="remove" className="text-wt-text-primary">Remove Tag</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-wt-text-primary">Tag Value</Label>
        <Input
          value={node.data.tag_value || ''}
          onChange={(e) => onUpdate(node.id, { tag_value: e.target.value })}
          placeholder="e.g., vip, high-value, cod-customer"
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
      </div>
      <div className="p-3 bg-teal-500/10 rounded-lg border border-teal-500/30">
        <p className="text-xs text-teal-300">Updates the customer record's tags list (not the order's).</p>
      </div>
    </div>
  );
}

function OrderNoteConfig({ node, onUpdate }) {
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-wt-text-primary">Note Text</Label>
        <Textarea
          value={node.data.note_text || ''}
          onChange={(e) => onUpdate(node.id, { note_text: e.target.value })}
          placeholder="e.g., Customer confirmed COD on {{date.today}}"
          rows={3}
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
        <p className="text-xs text-wt-text-muted mt-1">Supports variables like <code className="text-wt-gold">{'{{customer.full_name}}'}</code>.</p>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-wt-text-primary">Append to existing</Label>
          <p className="text-xs text-wt-text-muted">If off, replaces the current note.</p>
        </div>
        <input
          type="checkbox"
          checked={node.data.append !== false}
          onChange={(e) => onUpdate(node.id, { append: e.target.checked })}
          className="h-4 w-4 accent-wt-gold"
        />
      </div>
    </div>
  );
}

function HttpWebhookConfig({ node, onUpdate }) {
  const headersJson = JSON.stringify(node.data.webhook_headers || {}, null, 2);
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-wt-text-primary">URL</Label>
        <Input
          value={node.data.webhook_url || ''}
          onChange={(e) => onUpdate(node.id, { webhook_url: e.target.value })}
          placeholder="https://your-endpoint.com/hook"
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
      </div>
      <div>
        <Label className="text-wt-text-primary">Method</Label>
        <Select
          value={node.data.webhook_method || 'POST'}
          onValueChange={(v) => onUpdate(node.id, { webhook_method: v })}
        >
          <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="GET" className="text-wt-text-primary">GET</SelectItem>
            <SelectItem value="POST" className="text-wt-text-primary">POST</SelectItem>
            <SelectItem value="PUT" className="text-wt-text-primary">PUT</SelectItem>
            <SelectItem value="PATCH" className="text-wt-text-primary">PATCH</SelectItem>
            <SelectItem value="DELETE" className="text-wt-text-primary">DELETE</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-wt-text-primary">Custom Headers (JSON, optional)</Label>
        <Textarea
          value={headersJson}
          onChange={(e) => {
            try {
              onUpdate(node.id, { webhook_headers: JSON.parse(e.target.value || '{}') });
            } catch {
              // Ignore invalid JSON while typing
            }
          }}
          rows={3}
          placeholder='{"X-API-Key":"xxx"}'
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1 font-mono text-xs"
        />
      </div>
      <div>
        <Label className="text-wt-text-primary">Body Template (optional)</Label>
        <Textarea
          value={node.data.webhook_body || ''}
          onChange={(e) => onUpdate(node.id, { webhook_body: e.target.value })}
          rows={4}
          placeholder='{"order_id":"{{order.number}}","total":"{{order.total}}"}'
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1 font-mono text-xs"
        />
        <p className="text-xs text-wt-text-muted mt-1">Leave empty to send the entity as JSON automatically.</p>
      </div>
      <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
        <p className="text-xs text-blue-300">
          The journey branches on the HTTP response: 2xx → On Success, anything else → On Failure.
        </p>
      </div>
    </div>
  );
}

function CreateTaskConfig({ node, onUpdate }) {
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-wt-text-primary">Task Title</Label>
        <Input
          value={node.data.task_title || ''}
          onChange={(e) => onUpdate(node.id, { task_title: e.target.value })}
          placeholder="e.g., Follow up with {{customer.full_name}}"
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
      </div>
      <div>
        <Label className="text-wt-text-primary">Description</Label>
        <Textarea
          value={node.data.task_description || ''}
          onChange={(e) => onUpdate(node.id, { task_description: e.target.value })}
          rows={3}
          placeholder="Order {{order.number}} needs manual review"
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
      </div>
      <div>
        <Label className="text-wt-text-primary">Assignee User ID (optional)</Label>
        <Input
          value={node.data.assignee_id || ''}
          onChange={(e) => onUpdate(node.id, { assignee_id: e.target.value })}
          placeholder="Leave empty for unassigned"
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
      </div>
      <div>
        <Label className="text-wt-text-primary">Priority</Label>
        <Select
          value={node.data.priority || 'medium'}
          onValueChange={(v) => onUpdate(node.id, { priority: v })}
        >
          <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="low" className="text-wt-text-primary">Low</SelectItem>
            <SelectItem value="medium" className="text-wt-text-primary">Medium</SelectItem>
            <SelectItem value="high" className="text-wt-text-primary">High</SelectItem>
            <SelectItem value="urgent" className="text-wt-text-primary">Urgent</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function SimpleWebhookConfig({ node, onUpdate }) {
  const isSlack = node.data.action_type === 'send_slack';
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-wt-text-primary">{isSlack ? 'Slack' : 'Discord'} Webhook URL</Label>
        <Input
          value={node.data.webhook_url || ''}
          onChange={(e) => onUpdate(node.id, { webhook_url: e.target.value })}
          placeholder={isSlack ? 'https://hooks.slack.com/services/…' : 'https://discord.com/api/webhooks/…'}
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
      </div>
      <div>
        <Label className="text-wt-text-primary">Message</Label>
        <Textarea
          value={node.data.message || ''}
          onChange={(e) => onUpdate(node.id, { message: e.target.value })}
          rows={4}
          placeholder="New order {{order.number}} from {{customer.full_name}} — {{order.total}}"
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
        <p className="text-xs text-wt-text-muted mt-1">Variables work. Plain text only.</p>
      </div>
    </div>
  );
}





function DelayConfig({ node, onUpdate }) {
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-wt-text-primary">Delay Duration</Label>
        <div className="flex gap-2 mt-1">
          <Input
            type="number"
            min="1"
            value={node.data.delay_value || 1}
            onChange={(e) => onUpdate(node.id, { delay_value: parseInt(e.target.value) || 1 })}
            className="bg-wt-bg border-wt-border text-wt-text-primary w-24"
          />
          <Select
            value={node.data.delay_unit || 'hours'}
            onValueChange={(v) => onUpdate(node.id, { delay_unit: v })}
          >
            <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary flex-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-wt-surface border-wt-border">
              <SelectItem value="minutes" className="text-wt-text-primary">Minutes</SelectItem>
              <SelectItem value="hours" className="text-wt-text-primary">Hours</SelectItem>
              <SelectItem value="days" className="text-wt-text-primary">Days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
        <p className="text-xs text-blue-400">
          The journey will pause for the specified duration before continuing to the next step.
        </p>
      </div>
    </div>
  );
}

function ConditionConfig({ node, onUpdate }) {
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-wt-text-primary">Condition Field</Label>
        <Select
          value={node.data.condition_field || 'order.total'}
          onValueChange={(v) => onUpdate(node.id, { condition_field: v })}
        >
          <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            {CONDITION_FIELDS.map(f => (
              <SelectItem key={f.value} value={f.value} className="text-wt-text-primary">
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label className="text-wt-text-primary">Operator</Label>
        <Select
          value={node.data.condition_operator || '>'}
          onValueChange={(v) => onUpdate(node.id, { condition_operator: v })}
        >
          <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            {CONDITION_OPERATORS.map(o => (
              <SelectItem key={o.value} value={o.value} className="text-wt-text-primary">
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label className="text-wt-text-primary">Value</Label>
        {(() => {
          const fieldMeta = CONDITION_FIELDS.find(f => f.value === (node.data.condition_field || 'order.total'));
          const fieldType = fieldMeta?.type;
          // Boolean fields → true/false dropdown
          if (fieldType === 'boolean') {
            return (
              <Select
                value={node.data.condition_value?.toString() || ''}
                onValueChange={(value) => onUpdate(node.id, { condition_value: value })}
              >
                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
                  <SelectValue placeholder="Select value" />
                </SelectTrigger>
                <SelectContent className="bg-wt-surface border-wt-border">
                  <SelectItem value="true" className="text-wt-text-primary">True (Yes)</SelectItem>
                  <SelectItem value="false" className="text-wt-text-primary">False (No)</SelectItem>
                </SelectContent>
              </Select>
            );
          }
          // Enum fields → dropdown of allowed values
          if (fieldType && typeof fieldType === 'object' && Array.isArray(fieldType.enum)) {
            return (
              <Select
                value={node.data.condition_value || ''}
                onValueChange={(value) => onUpdate(node.id, { condition_value: value })}
              >
                <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
                  <SelectValue placeholder="Select value" />
                </SelectTrigger>
                <SelectContent className="bg-wt-surface border-wt-border">
                  {fieldType.enum.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-wt-text-primary">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          }
          // Number fields → numeric input
          if (fieldType === 'number') {
            return (
              <Input
                type="number"
                value={node.data.condition_value || ''}
                onChange={(e) => onUpdate(node.id, { condition_value: e.target.value })}
                placeholder="Enter a number"
                className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
              />
            );
          }
          // Default: plain text input
          return (
            <Input
              value={node.data.condition_value || ''}
              onChange={(e) => onUpdate(node.id, { condition_value: e.target.value })}
              placeholder="Enter value"
              className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
            />
          );
        })()}
      </div>
      
      <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
        <p className="text-xs text-purple-400">
          If the condition is TRUE, the "Yes" branch will be followed. Otherwise, the "No" branch will be used.
        </p>
      </div>
    </div>
  );
}

function EndConfig({ node }) {
  return (
    <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
      <p className="text-xs text-red-400">
        This marks the end of the journey. Contacts reaching this node will exit the automation.
      </p>
    </div>
  );
}

function WaitForResponseConfig({ node, onUpdate }) {
  return (
    <div className="space-y-4">
      <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
        <p className="text-xs text-yellow-300">
          ⚠️ This node only resumes when a response arrives via a WhatsApp or Email webhook.
          Place it <b>immediately after</b> a <b>Send WhatsApp</b> or <b>Send Email</b> action.
          Placing it after Delay / Tag / Webhook nodes will never receive events and will always time out.
        </p>
      </div>
      <div>
        <Label className="text-wt-text-primary">Expected Response Type</Label>
        <Select
          value={node.data.response_type || node.data.expected_response_type || 'location'}
          onValueChange={(value) => onUpdate(node.id, { response_type: value, expected_response_type: value })}
        >
          <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="location" className="text-wt-text-primary">📍 Location Pin</SelectItem>
            <SelectItem value="text" className="text-wt-text-primary">💬 Text Reply</SelectItem>
            <SelectItem value="image" className="text-wt-text-primary">🖼️ Image</SelectItem>
            <SelectItem value="button_reply" className="text-wt-text-primary">🔘 Quick Reply Button</SelectItem>
            <SelectItem value="any" className="text-wt-text-primary">📨 Any Response</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-wt-text-muted mt-1">What type of response are you expecting?</p>
      </div>
      
      <div>
        <Label className="text-wt-text-primary">Timeout (hours)</Label>
        <Input
          type="number"
          min="1"
          max="168"
          value={node.data.timeout_hours || 24}
          onChange={(e) => onUpdate(node.id, { timeout_hours: parseInt(e.target.value) || 24 })}
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
        <p className="text-xs text-wt-text-muted mt-1">How long to wait before timing out (1-168 hours)</p>
      </div>
      
      <div>
        <Label className="text-wt-text-primary">Node Label</Label>
        <Input
          value={node.data.label || ''}
          onChange={(e) => onUpdate(node.id, { label: e.target.value })}
          placeholder="e.g., Wait for Location"
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
      </div>
      
      <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
        <p className="text-xs text-cyan-400">
          <strong>Received:</strong> Green output - when customer sends the expected response<br/>
          <strong>Timeout:</strong> Red output - when no response is received within the timeout period
        </p>
      </div>
    </div>
  );
}

function UpdateDataConfig({ node, onUpdate }) {
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-wt-text-primary">Target Entity</Label>
        <Select
          value={node.data.target_entity || 'order'}
          onValueChange={(value) => onUpdate(node.id, { target_entity: value })}
        >
          <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="order" className="text-wt-text-primary">📦 Order</SelectItem>
            <SelectItem value="customer" className="text-wt-text-primary">👤 Customer</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label className="text-wt-text-primary">Target Field</Label>
        <Select
          value={node.data.target_field || 'delivery_location'}
          onValueChange={(value) => onUpdate(node.id, { target_field: value })}
        >
          <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="delivery_location" className="text-wt-text-primary">📍 Delivery Location</SelectItem>
            <SelectItem value="notes" className="text-wt-text-primary">📝 Notes</SelectItem>
            <SelectItem value="customer_response" className="text-wt-text-primary">💬 Customer Response</SelectItem>
            <SelectItem value="custom_field" className="text-wt-text-primary">🔧 Custom Field</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label className="text-wt-text-primary">Source Field</Label>
        <Select
          value={node.data.source_field || 'response.location'}
          onValueChange={(value) => onUpdate(node.id, { source_field: value })}
        >
          <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="response.location" className="text-wt-text-primary">📍 Response Location</SelectItem>
            <SelectItem value="response.text" className="text-wt-text-primary">💬 Response Text</SelectItem>
            <SelectItem value="response.lat" className="text-wt-text-primary">🌐 Latitude</SelectItem>
            <SelectItem value="response.lng" className="text-wt-text-primary">🌐 Longitude</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-wt-text-muted mt-1">Data from customer's response to save</p>
      </div>
      
      <div>
        <Label className="text-wt-text-primary">Node Label</Label>
        <Input
          value={node.data.label || ''}
          onChange={(e) => onUpdate(node.id, { label: e.target.value })}
          placeholder="e.g., Save Location to Order"
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
      </div>
      
      <div className="p-3 bg-teal-500/10 rounded-lg border border-teal-500/30">
        <p className="text-xs text-teal-400">
          This node saves the customer's response data to the specified field in the order or customer record.
        </p>
      </div>
    </div>
  );
}

function ButtonRouterConfig({ node, onUpdate, templates }) {
  const buttons = Array.isArray(node.data.buttons) ? node.data.buttons : [];
  
  // Walk upstream to find the WhatsApp template name, so we can auto-populate
  // the per-button handles from the templates list.
  const allNodes = useStore((s) => s.getNodes());
  const allEdges = useStore((s) => s.edges);
  const upstreamTemplate = useMemo(() => {
    const incoming = new Map();
    allEdges.forEach((e) => {
      if (!incoming.has(e.target)) incoming.set(e.target, []);
      incoming.get(e.target).push(e.source);
    });
    const visited = new Set();
    const queue = [...(incoming.get(node.id) || [])];
    while (queue.length) {
      const curr = queue.shift();
      if (!curr || visited.has(curr)) continue;
      visited.add(curr);
      const n = allNodes.find((x) => x.id === curr);
      if (!n) continue;
      const d = n.data || {};
      if (n.type === 'action' && (d.action_type === 'whatsapp' || !d.action_type)) {
        return { templateName: d.template_name || null, persistedQr: d.template_qr_buttons || null };
      }
      (incoming.get(curr) || []).forEach((p) => queue.push(p));
    }
    return { templateName: null, persistedQr: null };
  }, [node.id, allNodes, allEdges]);
  
  // Live-lookup QR buttons from the templates list based on the upstream name.
  const autoDetectedButtons = useMemo(() => {
    if (Array.isArray(upstreamTemplate.persistedQr) && upstreamTemplate.persistedQr.length > 0) {
      return upstreamTemplate.persistedQr;
    }
    const tplName = upstreamTemplate.templateName;
    if (!tplName || !Array.isArray(templates)) return [];
    const tpl = templates.find((t) => t.name === tplName);
    if (!tpl || !Array.isArray(tpl.components)) return [];
    const buttonsComp = tpl.components.find((c) => c.type === 'BUTTONS');
    return (buttonsComp?.buttons || [])
      .filter((b) => b.type === 'QUICK_REPLY' && b.text)
      .map((b) => b.text);
  }, [upstreamTemplate, templates]);
  
  const updateButton = (idx, value) => {
    const next = [...buttons];
    next[idx] = value;
    onUpdate(node.id, { buttons: next });
  };
  
  const addButton = () => {
    onUpdate(node.id, { buttons: [...buttons, ''] });
  };
  
  const removeButton = (idx) => {
    const next = buttons.filter((_, i) => i !== idx);
    onUpdate(node.id, { buttons: next });
  };
  
  const applyAutoDetected = () => {
    if (autoDetectedButtons.length > 0) {
      onUpdate(node.id, { buttons: autoDetectedButtons });
    }
  };
  
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-wt-text-primary">Node Label</Label>
        <Input
          value={node.data.label || ''}
          onChange={(e) => onUpdate(node.id, { label: e.target.value })}
          placeholder="e.g., Route by Feedback Reply"
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
      </div>
      
      <div>
        <Label className="text-wt-text-primary">Timeout (hours)</Label>
        <Input
          type="number"
          min={1}
          value={node.data.timeout_hours || 24}
          onChange={(e) => onUpdate(node.id, { timeout_hours: parseInt(e.target.value, 10) || 24 })}
          className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
        />
        <p className="text-xs text-wt-text-muted mt-1">
          If no button tap arrives in this window, the journey takes the Timeout path.
        </p>
      </div>
      
      {autoDetectedButtons.length > 0 && buttons.length === 0 && (
        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg space-y-2">
          <p className="text-xs text-green-400">
            Auto-detected {autoDetectedButtons.length} button{autoDetectedButtons.length > 1 ? 's' : ''} from upstream template
            {upstreamTemplate.templateName ? ` "${upstreamTemplate.templateName}"` : ''}:
          </p>
          <ul className="text-xs text-green-300 list-disc list-inside">
            {autoDetectedButtons.map((b, i) => (<li key={i}>{b}</li>))}
          </ul>
          <Button
            variant="outline"
            size="sm"
            onClick={applyAutoDetected}
            className="h-7 text-xs border-green-500/50 text-green-400 hover:bg-green-500/10"
            data-testid="button-router-apply-auto"
          >
            Use these buttons
          </Button>
        </div>
      )}
      
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-wt-text-primary">Button Labels</Label>
          <Button
            variant="outline"
            size="sm"
            onClick={addButton}
            className="h-7 text-xs border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
            data-testid="button-router-add-button"
          >
            + Add Button
          </Button>
        </div>
        
        {buttons.length === 0 ? (
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <p className="text-xs text-amber-400">
              {upstreamTemplate.templateName && autoDetectedButtons.length === 0
                ? `Could not auto-detect buttons from template "${upstreamTemplate.templateName}". If it has QUICK_REPLY buttons, re-select the template in the WhatsApp node above, or type each label manually here.`
                : 'Leave empty to auto-detect buttons from the upstream WhatsApp template, or type each button label manually.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {buttons.map((btn, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  value={btn}
                  onChange={(e) => updateButton(idx, e.target.value)}
                  placeholder={`Button ${idx + 1} label (e.g. Happy)`}
                  className="bg-wt-bg border-wt-border text-wt-text-primary flex-1"
                  data-testid={`button-router-input-${idx}`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeButton(idx)}
                  className="h-8 px-2 text-red-400 hover:bg-red-500/10"
                  data-testid={`button-router-remove-${idx}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
        <p className="text-xs text-purple-300">
          Each button label becomes its own output handle — draw an edge from it
          to whichever action should fire when the customer taps that button.
          Button matching is case-insensitive and ignores trailing punctuation.
        </p>
      </div>
    </div>
  );
}



const nodeIcons = {
  trigger: Zap,
  action: MessageCircle,
  delay: Clock,
  condition: GitBranch,
  end: Square,
  wait_for_response: MessageCircle,
  button_router: GitBranch,
  update_data: Database,
};

const nodeColors = {
  trigger: 'green',
  action: 'amber',
  delay: 'blue',
  condition: 'purple',
  end: 'red',
  wait_for_response: 'cyan',
  button_router: 'purple',
  update_data: 'teal',
};

export function NodeConfigPanel({ node, onUpdate, onDelete, onClose, templates, isReadOnly }) {
  const Icon = nodeIcons[node.type] || Zap;
  const color = nodeColors[node.type] || 'gray';
  
  const colorClasses = {
    green: 'text-green-400 bg-green-500/20',
    amber: 'text-wt-gold bg-amber-500/20',
    blue: 'text-blue-400 bg-blue-500/20',
    purple: 'text-purple-400 bg-purple-500/20',
    red: 'text-red-400 bg-red-500/20',
    cyan: 'text-cyan-400 bg-cyan-500/20',
    teal: 'text-teal-400 bg-teal-500/20',
  };

  return (
    <div className="w-80 h-full bg-wt-surface border-l border-wt-border flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-wt-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-wt-text-primary capitalize">
                {node.type} Node
              </h3>
              <p className="text-xs text-wt-text-muted">
                Configure this node
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-wt-bg rounded text-wt-text-muted hover:text-wt-text-primary"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      {/* Configuration Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {node.type === 'trigger' && (
          <TriggerConfig node={node} onUpdate={onUpdate} />
        )}
        {node.type === 'action' && (
          <ActionConfig node={node} onUpdate={onUpdate} templates={templates} />
        )}
        {node.type === 'delay' && (
          <DelayConfig node={node} onUpdate={onUpdate} />
        )}
        {node.type === 'condition' && (
          <ConditionConfig node={node} onUpdate={onUpdate} />
        )}
        {node.type === 'end' && (
          <EndConfig node={node} />
        )}
        {node.type === 'wait_for_response' && (
          <WaitForResponseConfig node={node} onUpdate={onUpdate} />
        )}
        {node.type === 'button_router' && (
          <ButtonRouterConfig node={node} onUpdate={onUpdate} templates={templates} />
        )}
        {node.type === 'update_data' && (
          <UpdateDataConfig node={node} onUpdate={onUpdate} />
        )}
      </div>
      
      {/* Footer Actions */}
      {!isReadOnly && node.type !== 'trigger' && (
        <div className="p-4 border-t border-wt-border">
          <Button
            variant="outline"
            onClick={() => onDelete(node.id)}
            className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Node
          </Button>
        </div>
      )}
    </div>
  );
}

export default NodeConfigPanel;
